import type { BindingAnchor, BindingEndpoint, BoardObjectV2, SegmentBindings } from "@bezpart-board/shared";
import type { SceneObject } from "../SceneHost";
import { clonePoints, type PathPoint } from "../pathGeometry";
import { computeSegmentBBox, isSegmentObject } from "../segmentGeometry";
import { objectBounds, type Bounds } from "../selectionBounds";

export const BINDABLE_OBJECT_TYPES = new Set([
  "sticky",
  "text",
  "rect",
  "ellipse",
  "triangle",
  "diamond",
  "star",
  "geom",
  "image",
]);

export function isBindableObject(obj: SceneObject | BoardObjectV2 | null | undefined): boolean {
  if (!obj || obj.locked) return false;
  return BINDABLE_OBJECT_TYPES.has(obj.type);
}

function roundPoint(x: number, y: number): PathPoint {
  return [Math.round(x), Math.round(y)];
}

function nearestEdgePoint(b: Bounds, toward: PathPoint): PathPoint {
  const candidates: PathPoint[] = [
    roundPoint(b.x + b.w / 2, b.y),
    roundPoint(b.x + b.w / 2, b.y + b.h),
    roundPoint(b.x, b.y + b.h / 2),
    roundPoint(b.x + b.w, b.y + b.h / 2),
  ];
  let best = candidates[0]!;
  let bestDist = Infinity;
  for (const c of candidates) {
    const d = Math.hypot(c[0] - toward[0], c[1] - toward[1]);
    if (d < bestDist) {
      bestDist = d;
      best = c;
    }
  }
  return best;
}

export function anchorPointOnObject(
  obj: SceneObject | BoardObjectV2,
  anchor: BindingAnchor,
  toward?: PathPoint
): PathPoint {
  const b = objectBounds(obj);
  const cx = b.x + b.w / 2;
  const cy = b.y + b.h / 2;
  switch (anchor) {
    case "center":
      return roundPoint(cx, cy);
    case "top":
      return roundPoint(cx, b.y);
    case "bottom":
      return roundPoint(cx, b.y + b.h);
    case "left":
      return roundPoint(b.x, cy);
    case "right":
      return roundPoint(b.x + b.w, cy);
    case "auto":
    default:
      return toward ? nearestEdgePoint(b, toward) : roundPoint(cx, cy);
  }
}

export function resolveBindingEndpoint(
  objects: BoardObjectV2[],
  endpoint: BindingEndpoint | null | undefined,
  toward: PathPoint
): PathPoint | null {
  if (!endpoint?.objectId) return null;
  const target = objects.find((o) => o.id === endpoint.objectId);
  if (!target || !isBindableObject(target)) return null;
  return anchorPointOnObject(target, endpoint.anchor, toward);
}

export function resolveSegmentPoints(
  obj: SceneObject | BoardObjectV2,
  objects: BoardObjectV2[]
): [PathPoint, PathPoint] | null {
  if (!isSegmentObject(obj)) return null;
  const bindings = obj.bindings;
  const a = obj.points[0]!;
  const b = obj.points[obj.points.length - 1]!;
  let start: PathPoint = [a[0], a[1]];
  let end: PathPoint = [b[0], b[1]];

  if (bindings?.start) {
    const resolved = resolveBindingEndpoint(objects, bindings.start, end);
    if (resolved) start = resolved;
  }
  if (bindings?.end) {
    const resolved = resolveBindingEndpoint(objects, bindings.end, start);
    if (resolved) end = resolved;
  }

  return [start, end];
}

export function applySegmentPoints(obj: SceneObject, start: PathPoint, end: PathPoint): void {
  if (!isSegmentObject(obj)) return;
  obj.points = [start, end];
  const bbox = computeSegmentBBox(obj);
  obj.x = bbox.x;
  obj.y = bbox.y;
  obj.w = bbox.w;
  obj.h = bbox.h;
}

export function segmentGeometryPatch(obj: SceneObject): Record<string, unknown> {
  return {
    x: obj.x,
    y: obj.y,
    w: obj.w,
    h: obj.h,
    points: clonePoints(obj.points),
  };
}

export function resolveAndApplySegment(obj: SceneObject, objects: BoardObjectV2[]): boolean {
  const resolved = resolveSegmentPoints(obj, objects);
  if (!resolved) return false;
  const a = obj.points[0]!;
  const b = obj.points[obj.points.length - 1]!;
  if (a[0] === resolved[0][0] && a[1] === resolved[0][1] && b[0] === resolved[1][0] && b[1] === resolved[1][1]) {
    return false;
  }
  applySegmentPoints(obj, resolved[0], resolved[1]);
  return true;
}

export function segmentHasBindings(obj: SceneObject | BoardObjectV2): boolean {
  if (!isSegmentObject(obj)) return false;
  const b = obj.bindings;
  return Boolean(b?.start?.objectId || b?.end?.objectId);
}

export function computeBindingPatches(
  objects: BoardObjectV2[],
  changedIds: Set<string>
): { id: string; patch: Record<string, unknown> }[] {
  const patches: { id: string; patch: Record<string, unknown> }[] = [];

  for (const obj of objects) {
    if (!isSegmentObject(obj) || !segmentHasBindings(obj)) continue;
    const b = obj.bindings!;
    const affects =
      (b.start?.objectId && changedIds.has(b.start.objectId)) ||
      (b.end?.objectId && changedIds.has(b.end.objectId));
    if (!affects) continue;

    const clone = JSON.parse(JSON.stringify(obj)) as SceneObject;
    if (!resolveAndApplySegment(clone, objects)) continue;
    patches.push({ id: obj.id, patch: segmentGeometryPatch(clone) });
  }

  return patches;
}

export function clearBindingsToObject(
  objects: BoardObjectV2[],
  deletedId: string
): { id: string; patch: Record<string, unknown> }[] {
  const patches: { id: string; patch: Record<string, unknown> }[] = [];

  for (const obj of objects) {
    if (!isSegmentObject(obj) || !obj.bindings) continue;
    const b = obj.bindings;
    const clearStart = b.start?.objectId === deletedId;
    const clearEnd = b.end?.objectId === deletedId;
    if (!clearStart && !clearEnd) continue;

    const nextBindings: SegmentBindings = { ...b };
    if (clearStart) nextBindings.start = null;
    if (clearEnd) nextBindings.end = null;

    const hasAny = nextBindings.start?.objectId || nextBindings.end?.objectId;
    patches.push({
      id: obj.id,
      patch: hasAny ? { bindings: nextBindings } : { bindings: null },
    });
  }

  return patches;
}

export function mergeBindingsPatch(
  current: SegmentBindings | undefined,
  patch: SegmentBindings | null | undefined
): SegmentBindings | undefined {
  if (patch === undefined) return current;
  if (patch === null) return undefined;
  const next = { ...(current ?? {}), ...patch };
  if (!next.start?.objectId && !next.end?.objectId) return undefined;
  return next;
}
